import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Sunrise, Sunset, Compass, CloudSun, Loader2, RefreshCw, Trash2, ExternalLink, ImageIcon, MapPin, Sun, Camera, Aperture,
  ListChecks, Shirt, CloudRain, Clock, AlertTriangle, Pin, Sparkles, FileText, Wind, Droplets, Thermometer,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { toast } from '@/components/ui/sonner';
import { SEO } from '@/components/SEO';
import {
  adminGetScout, adminUpdateScout, adminDeleteScout, adminRegenerateScout, adminRegenerateScoutImage, adminScoutReferencePhotos,
  adminScoutPhotoUrl, adminListBookings, mediaUrl,
} from '@/lib/api';
import { formatSessionDate } from '@/lib/money';
import { SCOUT_REPORT } from '@/constants/testIds';
import { ShootSimulatorSection } from '@/components/scout/ShootSimulatorSection';
import { keyedList } from '@/lib/keys';

const NONE = '__none__';

// ---------- helpers ----------
const minutesOf = (iso) => {
  if (!iso) return null;
  const [h, m] = iso.slice(11, 16).split(':').map(Number);
  return h * 60 + m;
};
const pct = (mins) => `${Math.max(0, Math.min(100, (mins / 1440) * 100))}%`;

// ---------- widgets ----------
function SunTimeline({ solar, sunAt }) {
  const rise = minutesOf(solar?.sunrise);
  const set = minutesOf(solar?.sunset);
  const session = sunAt ? minutesOf(sunAt.time) : null;
  const windows = (solar?.windows || []).filter((w) => w.start && w.end);
  return (
    <div data-testid={SCOUT_REPORT.sunTimeline} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] flex items-center gap-2"><Sun className="h-4 w-4 text-[hsl(var(--lxra-gold))]" /> Light timeline</p>
        <p className="text-[11px] text-[hsl(var(--lxra-muted))]">{solar?.day_length_minutes ? `${Math.floor(solar.day_length_minutes / 60)}h ${solar.day_length_minutes % 60}m of daylight` : ''}</p>
      </div>
      <div className="relative h-8 rounded-full bg-[#141416] overflow-hidden">
        {rise != null && set != null && (
          <div className="absolute inset-y-0 bg-[#1E1B14]" style={{ left: pct(rise), width: `calc(${pct(set)} - ${pct(rise)})` }} />
        )}
        {windows.map((w) => {
          const s = minutesOf(w.start); const e = minutesOf(w.end);
          if (s == null || e == null || e <= s) return null;
          return (
            <div
              key={w.label}
              title={`${w.label}: ${w.start_clock} – ${w.end_clock}`}
              className={`absolute inset-y-0 ${w.kind === 'golden' ? 'bg-[hsl(var(--lxra-gold))]/80' : 'bg-sky-500/60'}`}
              style={{ left: pct(s), width: `calc(${pct(e)} - ${pct(s)})` }}
            />
          );
        })}
        {session != null && (
          <motion.div
            initial={{ opacity: 0, scaleY: 0.4 }}
            animate={{ opacity: 1, scaleY: 1 }}
            className="absolute inset-y-0 w-0.5 bg-white shadow-[0_0_8px_rgba(255,255,255,0.8)]"
            style={{ left: pct(session) }}
          />
        )}
      </div>
      <div className="mt-2 flex justify-between text-[10px] text-[hsl(var(--lxra-muted))]">
        <span>12 AM</span><span>6 AM</span><span>12 PM</span><span>6 PM</span><span>12 AM</span>
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <div className="rounded-xl bg-[#141416] p-3 flex items-center gap-2"><Sunrise className="h-4 w-4 text-[hsl(var(--lxra-gold))]" /><div><p className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Sunrise</p><p className="text-[hsl(var(--lxra-fg))]">{solar?.sunrise_clock || '—'}</p></div></div>
        <div className="rounded-xl bg-[#141416] p-3 flex items-center gap-2"><Sun className="h-4 w-4 text-[hsl(var(--lxra-gold))]" /><div><p className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Solar noon</p><p className="text-[hsl(var(--lxra-fg))]">{solar?.solar_noon_clock || '—'} · {solar?.max_elevation}°</p></div></div>
        <div className="rounded-xl bg-[#141416] p-3 flex items-center gap-2"><Sunset className="h-4 w-4 text-[hsl(var(--lxra-gold))]" /><div><p className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Sunset</p><p className="text-[hsl(var(--lxra-fg))]">{solar?.sunset_clock || '—'}</p></div></div>
        <div className="rounded-xl bg-[#141416] p-3 flex items-center gap-2"><Clock className="h-4 w-4 text-white" /><div><p className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Your start</p><p className="text-[hsl(var(--lxra-fg))]">{sunAt?.clock || 'Open'}</p></div></div>
      </div>
      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[hsl(var(--lxra-muted))]">
        {windows.map((w) => (
          <li key={w.label} className="inline-flex items-center gap-1.5"><span className={`h-1.5 w-1.5 rounded-full ${w.kind === 'golden' ? 'bg-[hsl(var(--lxra-gold))]' : 'bg-sky-400'}`} />{w.label} {w.start_clock}–{w.end_clock}</li>
        ))}
      </ul>
    </div>
  );
}

// Sun compass geometry (pure helpers, hoisted so hooks can depend only on data).
const COMPASS_R = 92;
const COMPASS_C = 110;
const compassXY = (az, r) => {
  const a = ((az - 90) * Math.PI) / 180;
  return [COMPASS_C + r * Math.cos(a), COMPASS_C + r * Math.sin(a)];
};
// Elevation shrinks the radius: horizon = outer ring, zenith = center.
const compassPt = (p) => compassXY(p.azimuth, COMPASS_R * (1 - Math.max(0, p.elevation) / 90));

function SunCompass({ sunAt, track }) {
  const R = COMPASS_R; const C = COMPASS_C;
  const toXY = compassXY;
  const pt = compassPt;
  const path = useMemo(() => {
    const pts = (track || []).filter((p) => p.elevation > 0).map(compassPt);
    return pts.length ? `M ${pts.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(' L ')}` : '';
  }, [track]);
  const sun = sunAt ? pt(sunAt) : null;
  // Subject stands toward the sun (backlit), camera opposite.
  const subject = sunAt ? toXY(sunAt.azimuth, 34) : null;
  const camera = sunAt ? toXY(sunAt.azimuth + 180, 34) : null;
  return (
    <div data-testid={SCOUT_REPORT.sunCompass} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-5 flex flex-col">
      <p className="text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] flex items-center gap-2"><Compass className="h-4 w-4 text-[hsl(var(--lxra-gold))]" /> Sun compass</p>
      <div className="flex-1 flex items-center justify-center mt-2">
        <svg viewBox="0 0 220 220" className="w-full max-w-[260px]" role="img" aria-label="Sun position compass">
          <circle cx={C} cy={C} r={R} fill="#141416" stroke="hsl(var(--lxra-border))" />
          <circle cx={C} cy={C} r={R * 0.66} fill="none" stroke="hsl(var(--lxra-border))" strokeDasharray="2 4" />
          <circle cx={C} cy={C} r={R * 0.33} fill="none" stroke="hsl(var(--lxra-border))" strokeDasharray="2 4" />
          {['N', 'E', 'S', 'W'].map((d, i) => {
            const [x, y] = toXY(i * 90, R + 12);
            return <text key={d} x={x} y={y + 4} textAnchor="middle" fontSize="10" fill={d === 'N' ? 'hsl(var(--lxra-gold))' : '#A9A29A'} fontWeight={d === 'N' ? 700 : 400}>{d}</text>;
          })}
          {path && <path d={path} fill="none" stroke="hsl(var(--lxra-gold))" strokeOpacity="0.5" strokeWidth="1.5" />}
          {sun && subject && camera && (
            <>
              <line x1={camera[0]} y1={camera[1]} x2={sun[0]} y2={sun[1]} stroke="#F5F1E8" strokeOpacity="0.35" strokeDasharray="3 3" />
              <circle cx={subject[0]} cy={subject[1]} r="6" fill="#F5F1E8" />
              <text x={subject[0]} y={subject[1] - 10} textAnchor="middle" fontSize="8" fill="#F5F1E8">SUBJECT</text>
              <rect x={camera[0] - 6} y={camera[1] - 5} width="12" height="10" rx="2" fill="#0A0A0A" stroke="#F5F1E8" />
              <text x={camera[0]} y={camera[1] + 17} textAnchor="middle" fontSize="8" fill="#F5F1E8">YOU</text>
              <circle cx={sun[0]} cy={sun[1]} r="9" fill="hsl(var(--lxra-gold))" />
              <circle cx={sun[0]} cy={sun[1]} r="14" fill="none" stroke="hsl(var(--lxra-gold))" strokeOpacity="0.4" />
            </>
          )}
        </svg>
      </div>
      {sunAt ? (
        <p className="mt-2 text-xs text-[hsl(var(--lxra-muted))] text-center">
          At {sunAt.clock}: sun <strong className="text-[hsl(var(--lxra-fg))]">{sunAt.direction}</strong> ({sunAt.azimuth}°), <strong className="text-[hsl(var(--lxra-fg))]">{sunAt.elevation}°</strong> up — {sunAt.phase}. Backlight: subject faces you with the sun behind them.
        </p>
      ) : (
        <p className="mt-2 text-xs text-[hsl(var(--lxra-muted))] text-center">Gold arc shows the sun&apos;s path across the day. Set a start time to plot positions.</p>
      )}
    </div>
  );
}

function WeatherCard({ weather }) {
  return (
    <div data-testid={SCOUT_REPORT.weatherCard} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-5">
      <p className="text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] flex items-center gap-2"><CloudSun className="h-4 w-4 text-[hsl(var(--lxra-gold))]" /> Forecast</p>
      {weather?.available ? (
        <div className="mt-3 space-y-3">
          <p className="font-display text-2xl text-[hsl(var(--lxra-fg))] tracking-[0.02em]">{weather.summary}</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 text-[hsl(var(--lxra-muted))]"><Thermometer className="h-3.5 w-3.5" /> {Math.round(weather.temp_high_f)}° / {Math.round(weather.temp_low_f)}°</span>
            <span className="inline-flex items-center gap-1.5 text-[hsl(var(--lxra-muted))]"><CloudSun className="h-3.5 w-3.5" /> {weather.cloud_cover_mean}% cloud</span>
            <span className="inline-flex items-center gap-1.5 text-[hsl(var(--lxra-muted))]"><Droplets className="h-3.5 w-3.5" /> {weather.precip_probability_max}% rain</span>
            <span className="inline-flex items-center gap-1.5 text-[hsl(var(--lxra-muted))]"><Wind className="h-3.5 w-3.5" /> {Math.round(weather.wind_max_mph)} mph</span>
          </div>
          {weather.at_session && (
            <p className="rounded-xl bg-[#141416] p-3 text-xs text-[hsl(var(--lxra-muted))]">At your start: <span className="text-[hsl(var(--lxra-fg))]">{weather.at_session.summary}</span>, {weather.at_session.cloud_cover}% cloud, {Math.round(weather.at_session.temp_f)}°F</p>
          )}
        </div>
      ) : (
        <p className="mt-3 text-sm text-[hsl(var(--lxra-muted))]">{weather?.reason || 'Forecast not available yet.'} Scout planned for the season instead.</p>
      )}
    </div>
  );
}

function Section({ id, icon: Icon, title, children }) {
  return (
    <section data-testid={SCOUT_REPORT.section(id)} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-5 sm:p-6">
      <h2 className="font-display text-lg tracking-[0.04em] text-[hsl(var(--lxra-fg))] flex items-center gap-2 mb-4"><Icon className="h-4 w-4 text-[hsl(var(--lxra-gold))]" /> {title}</h2>
      {children}
    </section>
  );
}

const PRIORITY = { primary: 'border-[hsl(var(--lxra-gold))]/50 text-[hsl(var(--lxra-gold))]', secondary: 'border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))]', avoid: 'border-red-400/40 text-red-300' };

// ---------- page ----------
export default function ScoutReport() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refPhotos, setRefPhotos] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => adminGetScout(id).then((d) => { setData(d); setError(null); }).catch((e) => setError(e?.response?.status === 404 ? 'This scout report no longer exists.' : 'Could not load the report.')), [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { adminListBookings().then(setBookings).catch(() => setBookings([])); }, []);
  const hasReport = Boolean(data);
  const placeId = data?.place?.google_place_id;
  useEffect(() => {
    if (!hasReport) return;
    adminScoutReferencePhotos(id).then(setRefPhotos).catch(() => setRefPhotos({ available: false, photos: [] }));
  }, [id, hasReport, placeId]);

  // Poll while generating (text or image).
  useEffect(() => {
    if (!data) return undefined;
    const simStatus = data.simulation?.status;
    const simBusy = simStatus === 'generating' || simStatus === 'rendering'
      || [...(data.simulation?.frames || []), ...(data.simulation?.timelapse || [])].some((f) => f.status === 'pending' || f.status === 'rendering');
    const pending = data.status === 'generating' || data.concept_image_status === 'pending' || simBusy;
    if (!pending) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [data, load]);

  const pin = async (value) => {
    try {
      const updated = await adminUpdateScout(id, { booking_id: value === NONE ? '' : value });
      setData(updated);
      toast.success(value === NONE ? 'Unpinned from booking.' : 'Pinned — it will be ready on shoot day.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update.'); }
  };
  const regenImage = async () => {
    setBusy(true);
    try { await adminRegenerateScoutImage(id); setData((d) => ({ ...d, concept_image_status: 'pending' })); toast.success('Rendering a new concept…'); }
    catch { toast.error('Could not start the render.'); } finally { setBusy(false); }
  };
  const regen = async () => {
    if (!window.confirm('Re-run the written brief? The concept render stays.')) return;
    setBusy(true);
    try { await adminRegenerateScout(id); setData((d) => ({ ...d, status: 'generating' })); }
    catch { toast.error('Could not start regeneration.'); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this scout report?')) return;
    try { await adminDeleteScout(id); window.location.assign('/admin'); } catch { toast.error('Could not delete.'); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[hsl(var(--lxra-fg))] flex items-center justify-center p-6">
        <div className="text-center" data-testid={SCOUT_REPORT.error}>
          <AlertTriangle className="h-8 w-8 mx-auto text-[hsl(var(--lxra-gold))]" />
          <p className="mt-3 text-sm">{error}</p>
          <Link to="/admin" className="mt-4 inline-flex text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-gold))]">Back to dashboard</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-[#0A0A0A] p-6 max-w-6xl mx-auto space-y-4"><Skeleton className="h-10 w-1/2 bg-[#141416]" /><Skeleton className="h-72 w-full bg-[#141416] rounded-2xl" /></div>;
  }

  const r = data.report || {};
  const req = data.request || {};
  const concept = mediaUrl(data.concept_image_path);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[hsl(var(--lxra-fg))] pb-20" data-testid={SCOUT_REPORT.root}>
      <SEO title={`Scout — ${data.place?.name || 'Location'}`} description="LXRA Location Scout brief." />
      <header className="sticky top-0 z-40 bg-[#0A0A0A]/90 backdrop-blur border-b border-[hsl(var(--lxra-border))]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <Link to="/admin" data-testid={SCOUT_REPORT.backLink} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))]"><ArrowLeft className="h-4 w-4" /> Dashboard</Link>
          <div className="flex items-center gap-2">
            <Badge data-testid={SCOUT_REPORT.status} variant="outline" className={`text-[10px] gap-1 ${data.status === 'ready' ? 'border-emerald-400/40 text-emerald-400' : data.status === 'failed' ? 'border-red-400/40 text-red-300' : 'border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))]'}`}>
              {data.status === 'generating' && <Loader2 className="h-3 w-3 animate-spin" />}{data.status}
            </Badge>
            {data.booking_id && <Badge data-testid={SCOUT_REPORT.pinnedBadge} variant="outline" className="text-[10px] gap-1 border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))]"><Pin className="h-3 w-3" /> Pinned</Badge>}
            <span className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))] hidden sm:inline">Location Scout</span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-6 space-y-6">
        {/* Title row */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.14em] text-[hsl(var(--lxra-muted))] flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /> {data.place?.address}</p>
            <h1 data-testid={SCOUT_REPORT.title} className="font-display text-3xl sm:text-4xl leading-none tracking-[0.02em] mt-2">{data.place?.name}</h1>
            {data.place?.query && data.place.query_used && data.place.query.toLowerCase() !== data.place.query_used.toLowerCase() && (
              <p className="text-[11px] text-[hsl(var(--lxra-muted))] mt-1.5">You wrote &ldquo;{data.place.query}&rdquo; — resolved via &ldquo;{data.place.query_used}&rdquo;. Not right? Scout again with a more specific spot.</p>
            )}
            <p className="text-sm text-[hsl(var(--lxra-muted))] mt-2">
              {req.occasion || 'Session'} · {formatSessionDate(req.date)}{req.time ? ` · ${req.time}` : ' · time open'}{req.subjects ? ` · ${req.subjects}` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {data.place?.maps_url && (
              <a href={data.place.maps_url} target="_blank" rel="noreferrer" data-testid={SCOUT_REPORT.mapsLink} className="inline-flex items-center h-9 px-3 rounded-lg border border-[hsl(var(--lxra-border))] text-xs uppercase tracking-[0.08em] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors"><ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Map</a>
            )}
            {data.blueprint_id && (
              <Link to={`/admin/shoot/${data.blueprint_id}`} data-testid={SCOUT_REPORT.shootModeLink} className="inline-flex items-center h-9 px-3 rounded-lg border border-[hsl(var(--lxra-border))] text-xs uppercase tracking-[0.08em] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors"><ListChecks className="h-3.5 w-3.5 mr-1.5" /> Shoot Mode</Link>
            )}
            <Select value={data.booking_id || NONE} onValueChange={pin}>
              <SelectTrigger data-testid={SCOUT_REPORT.pinSelect} className="h-9 w-56 bg-[#141416] border-[hsl(var(--lxra-border))] text-xs text-[hsl(var(--lxra-fg))]"><Pin className="h-3.5 w-3.5 mr-1.5 text-[hsl(var(--lxra-gold))]" /><SelectValue placeholder="Pin to booking" /></SelectTrigger>
              <SelectContent className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
                <SelectItem value={NONE}>Not pinned</SelectItem>
                {bookings.filter((b) => b.status !== 'cancelled').map((b) => <SelectItem key={b.id} value={b.id}>{b.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" onClick={remove} data-testid={SCOUT_REPORT.deleteButton} className="h-9 w-9 text-[hsl(var(--lxra-muted))] hover:text-red-400 hover:bg-red-400/10"><Trash2 className="h-4 w-4" /></Button>
          </div>
        </div>

        {/* Visuals side by side */}
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-[hsl(var(--lxra-border))]">
              <p className="text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] flex items-center gap-2"><ImageIcon className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /> The real place</p>
              {refPhotos?.available && refPhotos.maps_url && <a href={refPhotos.maps_url} target="_blank" rel="noreferrer" className="text-[10px] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))]">Google Maps</a>}
            </div>
            {refPhotos === null ? (
              <Skeleton className="aspect-[16/10] w-full bg-[#141416] rounded-none" />
            ) : refPhotos.available && refPhotos.photos.length > 0 ? (
              <div data-testid={SCOUT_REPORT.referencePhotos} className="grid grid-cols-3 gap-0.5 aspect-[16/10]">
                {refPhotos.photos.slice(0, 3).map((p, i) => (
                  <figure key={p.name} className={`relative overflow-hidden ${i === 0 ? 'col-span-2 row-span-2' : ''}`}>
                    <img src={adminScoutPhotoUrl(p.name, i === 0 ? 1400 : 700)} alt={`${data.place?.name} reference ${i + 1}`} data-testid={SCOUT_REPORT.referencePhoto(i)} className="h-full w-full object-cover" loading="lazy" />
                    {p.attributions?.[0] && <figcaption className="absolute bottom-1 right-1.5 text-[9px] text-white/70 bg-black/40 px-1.5 rounded">© {p.attributions[0].name}</figcaption>}
                  </figure>
                ))}
              </div>
            ) : (
              <div data-testid={SCOUT_REPORT.referenceUnavailable} className="aspect-[16/10] flex flex-col items-center justify-center text-center p-6 bg-[radial-gradient(ellipse_at_center,rgba(201,164,92,0.08),transparent_70%)]">
                <MapPin className="h-7 w-7 text-[hsl(var(--lxra-gold))]" />
                <p className="mt-3 text-sm text-[hsl(var(--lxra-fg))]">{data.place?.name}</p>
                <p className="mt-1 text-[11px] text-[hsl(var(--lxra-muted))] max-w-xs">{refPhotos.reason}</p>
                <p className="mt-2 text-[11px] text-[hsl(var(--lxra-muted))]">{data.place?.lat?.toFixed(4)}, {data.place?.lon?.toFixed(4)} · {data.solar?.timezone}</p>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-[hsl(var(--lxra-border))]">
              <p className="text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /> Concept render · light &amp; placement</p>
              <button type="button" onClick={regenImage} disabled={busy || data.concept_image_status === 'pending'} data-testid={SCOUT_REPORT.regenerateImageButton} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.08em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] disabled:opacity-50">
                <RefreshCw className={`h-3 w-3 ${data.concept_image_status === 'pending' ? 'animate-spin' : ''}`} /> New render
              </button>
            </div>
            {data.concept_image_status === 'ready' && concept ? (
              <img src={concept} alt="AI concept render of the shoot setup" data-testid={SCOUT_REPORT.conceptImage} className="aspect-[16/10] w-full object-cover" />
            ) : data.concept_image_status === 'pending' ? (
              <div data-testid={SCOUT_REPORT.conceptPending} className="aspect-[16/10] flex flex-col items-center justify-center text-center p-6">
                <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--lxra-gold))]" />
                <p className="mt-3 text-xs text-[hsl(var(--lxra-muted))]">Rendering the light for {req.time || 'golden hour'}…</p>
              </div>
            ) : (
              <div data-testid={SCOUT_REPORT.conceptFailed} className="aspect-[16/10] flex flex-col items-center justify-center text-center p-6">
                <ImageIcon className="h-6 w-6 text-[hsl(var(--lxra-muted))]" />
                <p className="mt-3 text-xs text-[hsl(var(--lxra-muted))]">{data.concept_image_status === 'skipped' ? 'Concept render was skipped for this brief.' : `Render failed${data.concept_image_error ? `: ${data.concept_image_error}` : '.'}`}</p>
                <Button size="sm" variant="outline" onClick={regenImage} disabled={busy} className="mt-3 h-8 border-[hsl(var(--lxra-border))] text-xs">Render now</Button>
              </div>
            )}
          </div>
        </div>

        {/* Grounding widgets */}
        <div className="grid gap-4 lg:grid-cols-[1.4fr_0.8fr_0.8fr]">
          <SunTimeline solar={data.solar} sunAt={data.sun_at_session} />
          <SunCompass sunAt={data.sun_at_session} track={data.sun_track} />
          <WeatherCard weather={data.weather} />
        </div>

        {/* Report body */}
        {data.status === 'generating' ? (
          <div data-testid={SCOUT_REPORT.generating} className="rounded-2xl border border-[hsl(var(--lxra-gold))]/30 bg-[#0F0F10] p-8 text-center">
            <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--lxra-gold))] mx-auto" />
            <p className="mt-3 font-display text-lg tracking-[0.03em]">SCOUTING THE LIGHT…</p>
            <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1">Reading the sun path, forecast, and your kit. Usually under a minute.</p>
          </div>
        ) : data.status === 'failed' ? (
          <div className="rounded-2xl border border-red-400/30 bg-[#0F0F10] p-6 text-center">
            <AlertTriangle className="h-6 w-6 text-red-300 mx-auto" />
            <p className="mt-2 text-sm text-red-200">{data.error || 'Generation failed.'}</p>
            <Button onClick={regen} disabled={busy} data-testid={SCOUT_REPORT.regenerateButton} className="mt-4 h-9 bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold"><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Try again</Button>
          </div>
        ) : (
          <>
            <Section id="overview" icon={FileText} title="The Read">
              <p className="text-sm leading-relaxed text-[hsl(var(--lxra-fg))]">{r.overview}</p>
              {r.sun_strategy && <p className="mt-3 rounded-xl border-l-2 border-[hsl(var(--lxra-gold))] bg-[#141416] px-4 py-3 text-sm text-[hsl(var(--lxra-fg))]">{r.sun_strategy}</p>}
              {r.location_character?.length > 0 && (
                <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-[hsl(var(--lxra-muted))]">
                  {keyedList(r.location_character).map(({ key, item: b }) => <li key={key} className="flex gap-2"><span className="text-[hsl(var(--lxra-gold))]">·</span>{b}</li>)}
                </ul>
              )}
            </Section>

            {r.best_windows?.length > 0 && (
              <Section id="windows" icon={Clock} title="Best Windows">
                <div className="grid gap-3 sm:grid-cols-3">
                  {keyedList(r.best_windows, (w) => `${w.start}-${w.end}`).map(({ key, item: w }) => (
                    <div key={key} className={`rounded-xl border p-4 ${PRIORITY[w.priority] || PRIORITY.secondary}`}>
                      <p className="text-[10px] uppercase tracking-[0.12em]">{w.priority}</p>
                      <p className="font-display text-lg text-[hsl(var(--lxra-fg))] mt-1">{w.start} – {w.end}</p>
                      <p className="text-xs text-[hsl(var(--lxra-fg))] mt-0.5">{w.label}</p>
                      <p className="text-[11px] text-[hsl(var(--lxra-muted))] mt-2">{w.why}</p>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {r.lighting_setups?.length > 0 && (
              <Section id="lighting" icon={Sun} title="Lighting Setups">
                <div className="grid gap-3 lg:grid-cols-2">
                  {keyedList(r.lighting_setups, (s) => s.name).map(({ key, item: s }) => (
                    <div key={key} className="rounded-xl bg-[#141416] p-4 space-y-2">
                      <div className="flex items-start justify-between gap-2"><p className="text-sm text-[hsl(var(--lxra-fg))] font-medium">{s.name}</p>{s.when && <span className="text-[10px] text-[hsl(var(--lxra-gold))] whitespace-nowrap">{s.when}</span>}</div>
                      <dl className="grid grid-cols-[88px_1fr] gap-x-3 gap-y-1 text-xs">
                        <dt className="text-[hsl(var(--lxra-muted))]">Sun</dt><dd className="text-[hsl(var(--lxra-fg))]">{s.sun_position}</dd>
                        <dt className="text-[hsl(var(--lxra-muted))]">Subject</dt><dd className="text-[hsl(var(--lxra-fg))]">{s.subject_placement}</dd>
                        <dt className="text-[hsl(var(--lxra-muted))]">You</dt><dd className="text-[hsl(var(--lxra-fg))]">{s.camera_position}</dd>
                        {s.modifiers?.length > 0 && <><dt className="text-[hsl(var(--lxra-muted))]">Modifiers</dt><dd className="text-[hsl(var(--lxra-fg))]">{s.modifiers.join(' · ')}</dd></>}
                      </dl>
                      {s.look && <p className="text-[11px] italic text-[hsl(var(--lxra-muted))]">{s.look}</p>}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {r.shooting_spots?.length > 0 && (
              <Section id="spots" icon={MapPin} title="Where to Stand">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {keyedList(r.shooting_spots, (s) => s.name).map(({ key, item: s }, i) => (
                    <div key={key} className="rounded-xl border border-[hsl(var(--lxra-border))] p-4">
                      <p className="text-sm text-[hsl(var(--lxra-fg))] font-medium">{i + 1}. {s.name}</p>
                      <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1">{s.why}</p>
                      <div className="mt-3 flex flex-wrap gap-1.5 text-[10px]">
                        {s.face_direction && <span className="rounded-full border border-[hsl(var(--lxra-border))] px-2 py-0.5 text-[hsl(var(--lxra-fg))]">Face {s.face_direction}</span>}
                        {s.lens && <span className="rounded-full border border-[hsl(var(--lxra-border))] px-2 py-0.5 text-[hsl(var(--lxra-fg))]">{s.lens}</span>}
                        {s.angle && <span className="rounded-full border border-[hsl(var(--lxra-border))] px-2 py-0.5 text-[hsl(var(--lxra-fg))]">{s.angle}</span>}
                        {s.best_time && <span className="rounded-full border border-[hsl(var(--lxra-gold))]/40 px-2 py-0.5 text-[hsl(var(--lxra-gold))]">{s.best_time}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {r.camera_settings?.length > 0 && (
              <Section id="settings" icon={Aperture} title="Starting Settings">
                <div className="overflow-x-auto -mx-2">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[hsl(var(--lxra-border))] hover:bg-transparent">
                        {['Scenario', 'Body / Lens', 'Aperture', 'Shutter', 'ISO', 'WB', 'Focus', 'Notes'].map((h) => <TableHead key={h} className="text-[hsl(var(--lxra-muted))] text-[10px] uppercase tracking-[0.1em] whitespace-nowrap">{h}</TableHead>)}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {keyedList(r.camera_settings, (c) => c.scenario).map(({ key, item: c }) => (
                        <TableRow key={key} className="border-[hsl(var(--lxra-border))] hover:bg-[#141416]">
                          <TableCell className="text-xs text-[hsl(var(--lxra-fg))] min-w-[160px]">{c.scenario}</TableCell>
                          <TableCell className="text-xs text-[hsl(var(--lxra-muted))] min-w-[160px]">{c.body}<br />{c.lens}</TableCell>
                          <TableCell className="text-xs font-mono text-[hsl(var(--lxra-gold))]">{c.aperture}</TableCell>
                          <TableCell className="text-xs font-mono text-[hsl(var(--lxra-gold))]">{c.shutter}</TableCell>
                          <TableCell className="text-xs font-mono text-[hsl(var(--lxra-gold))]">{c.iso}</TableCell>
                          <TableCell className="text-xs text-[hsl(var(--lxra-muted))] whitespace-nowrap">{c.white_balance}</TableCell>
                          <TableCell className="text-xs text-[hsl(var(--lxra-muted))]">{c.focus}</TableCell>
                          <TableCell className="text-[11px] text-[hsl(var(--lxra-muted))] min-w-[220px]">{c.exposure_notes}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Section>
            )}

            <div className="grid gap-4 lg:grid-cols-2">
              {r.gear_checklist?.length > 0 && (
                <Section id="gear" icon={Camera} title="Pack List">
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-[hsl(var(--lxra-fg))]">
                    {keyedList(r.gear_checklist).map(({ key, item: g }) => <li key={key} className="flex gap-2"><span className="text-[hsl(var(--lxra-gold))]">·</span>{g}</li>)}
                  </ul>
                </Section>
              )}
              {r.wardrobe_and_direction?.length > 0 && (
                <Section id="wardrobe" icon={Shirt} title="Wardrobe & Direction">
                  <ul className="space-y-1.5 text-xs text-[hsl(var(--lxra-fg))]">
                    {keyedList(r.wardrobe_and_direction).map(({ key, item: g }) => <li key={key} className="flex gap-2"><span className="text-[hsl(var(--lxra-gold))]">·</span>{g}</li>)}
                  </ul>
                </Section>
              )}
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              {r.weather_plan && (
                <Section id="weather-plan" icon={CloudRain} title="Weather Plan">
                  <p className="text-sm text-[hsl(var(--lxra-fg))]">{r.weather_plan.read}</p>
                  <dl className="mt-3 space-y-2 text-xs">
                    {[['If overcast', r.weather_plan.if_overcast], ['If rain', r.weather_plan.if_rain], ['If harsh sun', r.weather_plan.if_harsh_sun]].filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} className="rounded-xl bg-[#141416] p-3"><dt className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-gold))]">{k}</dt><dd className="text-[hsl(var(--lxra-fg))] mt-0.5">{v}</dd></div>
                    ))}
                  </dl>
                </Section>
              )}
              {r.timeline?.length > 0 && (
                <Section id="timeline" icon={Clock} title="Run of Show">
                  <ol className="space-y-2">
                    {keyedList(r.timeline, (t) => `${t.time}-${t.item}`).map(({ key, item: t }) => (
                      <li key={key} className="flex gap-3 text-xs"><span className="w-20 shrink-0 font-mono text-[hsl(var(--lxra-gold))]">{t.time}</span><span className="text-[hsl(var(--lxra-fg))]">{t.item}</span></li>
                    ))}
                  </ol>
                </Section>
              )}
            </div>

            {r.risks?.length > 0 && (
              <Section id="risks" icon={AlertTriangle} title="Watch Out For">
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-[hsl(var(--lxra-muted))]">
                  {keyedList(r.risks).map(({ key, item: g }) => <li key={key} className="flex gap-2"><span className="text-amber-300">·</span>{g}</li>)}
                </ul>
              </Section>
            )}

            <ShootSimulatorSection report={data} onChanged={load} />

            <p className="text-[10px] text-[hsl(var(--lxra-muted))] text-center pt-2">
              Generated by {data.provider}/{data.model} in {(data.generation_ms / 1000).toFixed(0)}s · Solar data computed for {data.solar?.timezone} · Scout is assistive — you make the call on set.
              <button type="button" onClick={regen} disabled={busy} data-testid={SCOUT_REPORT.regenerateButton} className="ml-2 underline hover:text-[hsl(var(--lxra-gold))]">Re-run brief</button>
            </p>
          </>
        )}
      </main>
    </div>
  );
}
